import React, { FC, useEffect, useRef } from "react";
import * as d3 from "d3";

export type VisualizationSettings = {
  chartWidth: number;
  chartHeight: number;
  chartPadding: { left: number; top: number; right: number; bottom: number };
  lineColor: string;
  lineWidth: number;
  bins: d3.TimeInterval | number;
  yDomain?: [number, number];
  xAxisHeight: number;
};

export const VisualizationSettingsDefaults: VisualizationSettings = {
  chartWidth: 256,
  chartHeight: 32,
  chartPadding: { left: 2, top: 2, right: 2, bottom: 0 },
  lineColor: "white",
  lineWidth: 1.5,
  bins: d3.timeMonth.every(3) || d3.timeMonth,
  xAxisHeight: 10,
};

type SparkLinePoint = {
  value: number;
  date: Date;
};

// Component-specific props.
type ComponentProps = {
  data: SparkLinePoint[];
  settings: Partial<VisualizationSettings>;
};

const SparkLine: FC<ComponentProps> = (props: ComponentProps) => {
  const settings = getSettings();

  const chartWidth = settings.chartWidth - settings.chartPadding.left - settings.chartPadding.right;
  const chartHeight = settings.chartHeight - settings.chartPadding.top - settings.chartPadding.bottom;

  const { xScale, yScale, xAxis } = getScales(props.data, chartWidth, chartHeight);

  const xaxisRef = useRef<SVGGElement>(null);

  // setup line generator
  const lineGenerator = d3
    .line<SparkLinePoint>()
    .x((d) => xScale(d.date))
    .y((d) => yScale(d.value))
    .curve(d3.curveBasis);

  function getScales(data: SparkLinePoint[], chartWidth: number, chartHeight: number) {
    const { rangeX, rangeY } = computeDataRange(data);

    const xScale = d3.scaleTime().domain(rangeX).range([0, chartWidth]);
    const yScale = d3.scaleLinear<number>().domain(rangeY).range([chartHeight, 0]);

    const day = 24 * 60 * 60 * 1000;
    const month = day * 31;

    const xAxis: d3.Axis<Date | d3.NumberValue> = d3.axisTop(xScale);
    xAxis.tickSize(12);

    const diff = xScale.domain()[1].getTime() - xScale.domain()[0].getTime();
    xAxis.tickArguments(diff > month ? [] : [d3.timeDay.every(1)]);

    return {
      xScale,
      yScale,
      xAxis,
    };
  }

  function computeDataRange(data: SparkLinePoint[]) {
    let minX = data[0].date;
    let maxX = data[0].date;
    let minY = data[0].value;
    let maxY = data[0].value;

    for (const point of data) {
      if (point.date < minX) {
        minX = point.date;
      }
      if (point.date > maxX) {
        maxX = point.date;
      }
      if (point.value < minY) {
        minY = point.value;
      }
      if (point.value > maxY) {
        maxY = point.value;
      }
    }
    return {
      rangeX: [minX, maxX],
      rangeY: [minY, maxY],
    };
  }

  function getTransition() {
    return d3.transition().duration(0).ease(d3.easeLinear);
  }

  function renderXAxis(node: SVGGElement | null) {
    console.log("renderXAxis");

    const dataJoin = d3.select(node).selectAll<SVGGElement, unknown>(".xAxis").data([{ xAxis }]);

    const dataEnter = dataJoin
      .enter()
      .append("g")
      .attr("class", "xAxis")
      .attr("stroke-width", "0px")
      .attr("width", settings.chartWidth)
      .attr("height", settings.xAxisHeight);

    dataEnter.merge(dataJoin).transition(getTransition()).call(xAxis);

    dataEnter
      .merge(dataJoin)
      .selectAll("text")
      .attr("font-size", "min(1.75vw, 0.75em)")
      .attr("transform", "translate(0,0)");

    dataJoin.exit().remove();
  }

  function getSettings(): VisualizationSettings {
    return {
      ...VisualizationSettingsDefaults,
      ...props.settings,
    };
  }

  useEffect(() => {
    renderXAxis(xaxisRef.current);
  }, [props.data, props.settings, settings.bins]);

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${settings.chartWidth} ${
        settings.chartHeight + settings.xAxisHeight + settings.chartPadding.bottom + settings.chartPadding.top
      }`}
    >
      <g
        className="chart"
        transform={`translate(${[settings.chartPadding.left, settings.chartPadding.top]})`}
        fill="grey"
      >
        <path
          stroke={settings.lineColor}
          strokeWidth={settings.lineWidth}
          strokeLinejoin="round"
          strokeLinecap="round"
          fill="none"
          d={lineGenerator(props.data) || ""}
        />
      </g>
      <g
        transform={`translate(${settings.chartPadding.left},${settings.chartHeight * 2 + settings.xAxisHeight})`}
        ref={xaxisRef}
      ></g>
    </svg>
  );
};

export default SparkLine;
